import React, { useState } from 'react';
import {
  useNavigate } from 'react-router-dom';
import { Box,
  CircularProgress,
  Stack,
  Tabs,
  useMediaQuery,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedTab as Tab, LocalizedTooltip as Tooltip } from './i18n/LocalizedMui';
import { useTheme } from '@mui/material/styles';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import { getDownloadFileName } from '../utils/downloadFileName';
import { normalizeLanguage } from '../i18n/languages';
import { resolvePdfLanguage, useUserPreferences } from '../contexts/UserPreferencesContext';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

type ActiveTab =
  | 'nadi'
  | 'kp'
  | 'autoBtr'
  | 'vedic'
  | 'faladesh'
  | 'remedies'
  | 'western'
  | 'advanced-tools'
  | 'jaimini'
  | 'tithiPravesh'
  | 'varshphal'
  | 'muhurat'
  | 'bnn'
  | 'panchPakshi'
  | 'chakra'
  | 'lalKitab';

interface Props {
  chartId: string;
  active: ActiveTab;
  isHoraryChart?: boolean;
}

const TABS: { key: ActiveTab; labelKey: string; path: (id: string) => string; premium?: boolean }[] = [
  { key: 'nadi',  labelKey: 'astrology.nadiAnalysis',  path: (id) => `/chart/${id}` },
  { key: 'kp',    labelKey: 'astrology.kpAnalysis',    path: (id) => `/chart/${id}/kp` },
  { key: 'autoBtr', labelKey: 'AUTO BTR', path: (id) => `/chart/${id}/rectification`, premium: true },
  { key: 'vedic', labelKey: 'astrology.vedicAnalysis', path: (id) => `/chart/${id}/divisional` },
  { key: 'bnn',   labelKey: 'astrology.bnnAnalysis',   path: (id) => `/chart/${id}/bnn` },
  { key: 'jaimini', labelKey: 'astrology.jaiminiAnalysis', path: (id) => `/chart/${id}/jaimini`, premium: true },
  { key: 'remedies', labelKey: 'yogas.remedies', path: (id) => `/chart/${id}/remedies`, premium: true },
  { key: 'lalKitab', labelKey: 'Lal Kitab', path: (id) => `/chart/${id}/lal-kitab`, premium: true },
  { key: 'tithiPravesh', labelKey: 'Tithi Pravesh', path: (id) => `/chart/${id}/tithi-pravesh`, premium: true },
  { key: 'varshphal', labelKey: 'Varshphal', path: (id) => `/chart/${id}/varshphal`, premium: true },
  { key: 'muhurat', labelKey: 'Muhurat', path: (id) => `/chart/${id}/muhurat`, premium: true },
  { key: 'western', labelKey: 'Western Astrology', path: (id) => `/chart/${id}/western`, premium: true },
  { key: 'advanced-tools', labelKey: 'nav.advancedTools', path: (id) => `/chart/${id}/advanced-tools`, premium: true },
  { key: 'panchPakshi', labelKey: 'astrology.panchPakshi', path: (id) => `/chart/${id}/panch-pakshi`, premium: true },
  { key: 'chakra', labelKey: 'Chakra', path: (id) => `/chart/${id}/chakra`, premium: true },
];

const AnalysisTabBar: React.FC<Props> = ({ chartId, active, isHoraryChart = false }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const appLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const { pdfLanguageSameAsApp, pdfLanguage } = useUserPreferences();
  const language = resolvePdfLanguage(appLanguage, pdfLanguageSameAsApp, pdfLanguage);
  const { state: authState } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [pdfLoading, setPdfLoading] = useState(false);
  const [clientPdfLoading, setClientPdfLoading] = useState(false);

  // On desktop the sidebar handles all chart navigation — hide this bar for logged-in users
  if (authState.isAuthenticated && isDesktop) return null;
  const visibleTabs = isHoraryChart ? TABS.filter((t) => t.key === 'nadi' || t.key === 'kp') : TABS;
  const activeIndex = visibleTabs.findIndex((t) => t.key === active);
  const safeValue = activeIndex >= 0 ? activeIndex : false;
  const isPremium = hasPremiumSubscription(authState.user);
  const canDownload = authState.isAuthenticated && isPremium && !!chartId && chartId !== 'preview';
  const lockedTooltip = !authState.isAuthenticated
    ? 'Login to choose a plan'
    : 'Choose Plan';
  const actionTooltip = canDownload ? '' : lockedTooltip;

  const downloadBlob = async (url: string, fileName: string) => {
    const response = await fetch(url, {
      headers: authState.token ? { Authorization: `Bearer ${authState.token}` } : {},
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.message || payload.error || 'Download failed');
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = getDownloadFileName(response, fileName);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handlePdfDownload = async () => {
    if (!canDownload) return;
    try {
      setPdfLoading(true);
      await downloadBlob(`${API_BASE_URL}/charts/${chartId}/pdf?language=${encodeURIComponent(language)}`, 'report.pdf');
    } catch (error: any) {
      window.alert(error.message || 'Could not download PDF report.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClientPdfDownload = async () => {
    if (!canDownload) return;
    try {
      setClientPdfLoading(true);
      await downloadBlob(`${API_BASE_URL}/charts/${chartId}/pdf?language=${encodeURIComponent(language)}&clientReport=1`, 'client_report.pdf');
    } catch (error: any) {
      window.alert(error.message || 'Could not download client report.');
    } finally {
      setClientPdfLoading(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, index: number) => {
    const tab = visibleTabs[index];
    if (!tab) return;
    if (tab.premium && !isPremium) {
      navigate(authState.user ? '/upgrade' : '/login');
      return;
    }
    navigate(tab.path(chartId));
  };

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        mb: 3,
        bgcolor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={{ xs: 0.75, sm: 1 }}
        sx={{ px: { xs: 0.5, sm: 0 }, pr: { sm: 1 }, py: { xs: 0.75, sm: 0 } }}
      >
        <Tabs
          value={safeValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          textColor="primary"
          indicatorColor="primary"
          sx={{
            flex: { sm: 1 },
            width: { xs: '100%', sm: 'auto' },
            minWidth: 0,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: { xs: '0.82rem', sm: '0.9rem' },
              minWidth: { xs: 104, sm: 120 },
              px: { xs: 1.25, sm: 2 },
            },
            '& .Mui-selected': {
              fontWeight: 600,
            },
          }}
        >
          {visibleTabs.map((tab) => {
            const locked = Boolean(tab.premium && !isPremium);
            const label = ['chakra', 'lalKitab', 'autoBtr'].includes(tab.key) ? tab.labelKey : t(tab.labelKey);
            return (
            <Tab
              key={tab.key}
              icon={locked ? <LockIcon fontSize="small" /> : undefined}
              iconPosition="start"
              label={label}
              sx={locked ? { color: 'warning.dark', '&.Mui-selected': { color: 'primary.main' } } : undefined}
            />
            );
          })}
        </Tabs>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: { xs: 'flex-end', sm: 'flex-start' },
            width: { xs: '100%', sm: 'auto' },
            px: { xs: 0.5, sm: 0 },
            pb: { xs: 0.25, sm: 0 },
          }}
        >
          <Tooltip title={actionTooltip || 'Download PDF Report'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={pdfLoading ? <CircularProgress size={14} /> : canDownload ? <PictureAsPdfIcon /> : <LockIcon />}
                disabled={!canDownload || pdfLoading}
                onClick={handlePdfDownload}
                sx={{ whiteSpace: 'nowrap', minWidth: { xs: 96, sm: 'auto' } }}
              >
                PDF
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={actionTooltip || 'Download Client Report'}>
            <span>
              <Button size="small" variant="outlined" color="warning" startIcon={clientPdfLoading ? <CircularProgress size={14} /> : canDownload ? <ContactPageIcon /> : <LockIcon />} disabled={!canDownload || clientPdfLoading} onClick={handleClientPdfDownload} sx={{ whiteSpace: 'nowrap', minWidth: { xs: 96, sm: 'auto' } }}>Client</Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export default AnalysisTabBar;
